import React from "react";
import { Empty, Modal, Spin } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useGetTaskExecutionByIdQuery } from "shared/api";

interface TaskExecutionFilesModalProps {
  open: boolean;
  taskExecutionId: number | null;
  onClose: () => void;
}

const getFileName = (url: string) => {
  try {
    const cleanUrl = url.split("?")[0];
    return decodeURIComponent(cleanUrl.split("/").pop() || "Unnamed file");
  } catch {
    return "Unnamed file";
  }
};

/** Read-only viewer for the files attached to a single task execution. */
export const TaskExecutionFilesModal: React.FC<
  TaskExecutionFilesModalProps
> = ({ open, taskExecutionId, onClose }) => {
  const { t } = useTranslation();

  const { data: detail, isFetching } = useGetTaskExecutionByIdQuery(
    { id: taskExecutionId as number, expand: "files" },
    { skip: !open || taskExecutionId == null }
  );

  const files = detail?.files ?? [];

  const renderContent = () => {
    if (isFetching) {
      return (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      );
    }

    if (files.length === 0) {
      return <Empty description={t("taskHub.myTasks.noFiles") || "No files"} />;
    }

    return (
      <ul className="space-y-2">
        {files.map((file) => (
          <li
            key={file.id}
            className="flex items-center justify-between bg-gray-50 p-2 rounded border"
          >
            <span className="truncate max-w-[80%]" title={file.file}>
              {getFileName(file.file)}
            </span>
            <a
              href={file.file}
              target="_blank"
              rel="noopener noreferrer"
              download
              title={t("taskHub.modal.downloadFile") || "Download"}
              className="text-blue-500 hover:text-blue-700"
            >
              <DownloadOutlined className="text-xl" />
            </a>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Modal
      title={t("taskHub.myTasks.files") || "Files"}
      open={open}
      onCancel={onClose}
      footer={null}
    >
      {renderContent()}
    </Modal>
  );
};
